import PropTypes from 'prop-types';
import React from 'react';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import { ICON_ALIASES } from '_constants/icons';
import { TASK_TYPE } from '_constants/tasks';
import { theme } from '_constants/theme';
import { hexToRGBA } from '_helpers/theme';
import { selectMergedFileTypeMapData } from '_store/fileTypes/selector';
import { selectActivePersonalTaxFilingProductId } from '_store/order/selector';

import Icon from 'components/atomic/Icon';
import UploadButton from 'components/atomic/UploadButton';
import Text from 'components/atomic/layouts/Text';

const StyledContainer = styled.div`
  display: grid;
  grid-template-columns: 48px auto 112px;

  margin-bottom: ${theme.spaces.small + 2}px;
  padding-bottom: ${theme.spaces.small + 2}px;

  border-bottom: 1px solid ${hexToRGBA(theme.colors.gray.secondary, 0.16)};
  cursor: pointer;

  > * {
    &:nth-child(1) {
      grid-row-start: 1;
      grid-row-end: 3;
    }

    &:nth-child(2) {
      grid-row-start: 1;
      grid-row-end: 2;
    }

    &:nth-child(3) {
      grid-row-start: 2;
      grid-row-end: 3;
    }

    &:nth-child(4) {
      grid-row-start: 1;
      grid-row-end: 3;
    }
  }

  @media ${theme.devices.tablet} {
    grid-template-columns: 48px auto;

    > * {
      &:nth-child(4) {
        grid-row-start: 4;
        grid-row-end: 4;
        grid-column-start: 2;
        grid-column-end: 3;
      }
    }
  }
`;

const StyledIconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 40px;
  width: 40px;
  border-radius: 50%;
  background: ${theme.colors.indigo.primary};

  svg {
    fill: ${theme.colors.white};
  }
`;

const StyledActionContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;

  @media ${theme.devices.tablet} {
    justify-content: flex-start;
    padding-top: ${theme.spaces.small}px;
  }
`;

const StyledActionButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 26px;
  padding: ${theme.spaces.smallX}px;
  border: 1px solid ${hexToRGBA(theme.colors.gray.secondary, 0.16)};
  border-radius: 8px;
  background: ${hexToRGBA(theme.colors.gray.secondary, 0.08)};
  cursor: pointer;

  &:hover {
    border: 1px solid ${hexToRGBA(theme.colors.indigo.primary, 0.16)};
    background: ${hexToRGBA(theme.colors.indigo.primary, 0.08)};

    > div {
      color: ${theme.colors.indigo.primary};
    }
  }

  ${({ disabled }) =>
    disabled &&
    css`
      pointer-events: none;
      opacity: 0.4;
    `}
`;

const TasksListItem = ({ id, productId, type, status, subject, message, fileRequest, onActionClick, ...restProps }) => {
  const activePersonalTaxFilingProductId = useSelector(selectActivePersonalTaxFilingProductId);
  const fileTypeMap = useSelector(selectMergedFileTypeMapData);

  const handleSendMessageClick = () => {
    onActionClick && onActionClick({ id, productId, type, status, subject, message, fileRequest });
  };

  const handleUploadClick = (file) => {
    onActionClick && onActionClick({ id, productId, type, status, subject, message, fileRequest }, file);
  };

  const text = type === TASK_TYPE.fileRequest ? `Document is required` : subject;
  const subtext =
    type === TASK_TYPE.fileRequest ? (
      <span>
        Provide&nbsp;
        <b>
          {fileRequest?.year} {fileTypeMap[fileRequest?.type]}
        </b>
        &nbsp;document in order to continue&nbsp;
        {productId === activePersonalTaxFilingProductId ? 'Personal' : 'Business'}
        &nbsp;Tax Preparation
      </span>
    ) : (
      message
    );

  return (
    <StyledContainer {...restProps}>
      <StyledIconContainer>
        <Icon alias={ICON_ALIASES.checkbox} color={theme.colors.indigo.primary} />
      </StyledIconContainer>
      <Text size={theme.fontSizes.small} weight={600}>
        {text}
      </Text>
      <Text ignoreSpan size={theme.fontSizes.small} weight={400} color={theme.colors.gray.secondary}>
        {subtext}
      </Text>
      <StyledActionContainer>
        {type === TASK_TYPE.fileRequest && (
          <UploadButton
            renderAs={(props) => (
              <StyledActionButton {...props}>
                <Text size={theme.fontSizes.smallX} weight={600} color={theme.colors.gray.secondary}>
                  Upload Now
                </Text>
              </StyledActionButton>
            )}
            onChange={handleUploadClick}
          />
        )}
        {type === TASK_TYPE.textRequest && (
          <StyledActionButton onClick={handleSendMessageClick}>
            <Text size={theme.fontSizes.smallX} weight={600} color={theme.colors.gray.secondary}>
              Send Message
            </Text>
          </StyledActionButton>
        )}
      </StyledActionContainer>
    </StyledContainer>
  );
};

TasksListItem.propTypes = {
  id: PropTypes.string,
  productId: PropTypes.string,
  type: PropTypes.string,
  status: PropTypes.string,
  subject: PropTypes.string,
  message: PropTypes.string,
  fileRequest: PropTypes.object,
  onActionClick: PropTypes.func,
};

export default TasksListItem;
